import React, { useState, useEffect, useRef } from 'react';
import { useGame } from '../../context/GameContext';
import './SaveManager.css';

const SAVE_SLOTS = 3;

function SaveManager({ onNotification }) {
  const {
    getSaveData,
    loadSaveData,
    resetGame,
  } = useGame();

  const [isOpen, setIsOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'delete'|'overwrite'|'reset', slotIndex? }
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);

  // Load slot metadata on mount
  useEffect(() => {
    loadSlotMetadata();
    
    const saved = localStorage.getItem('medievalKingdomSave');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.savedAt) setLastAutoSave(data.savedAt);
      } catch (e) {
        console.warn('[SaveManager] Failed to parse autosave:', e.message);
      }
    }

    const interval = setInterval(() => {
      const saved = localStorage.getItem('medievalKingdomSave');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          if (data.savedAt) setLastAutoSave(data.savedAt);
        } catch (e) {
          console.warn('[SaveManager] Failed to parse autosave:', e.message);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
        setConfirmAction(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const loadSlotMetadata = () => {
    const loadedSlots = [];
    for (let i = 0; i < SAVE_SLOTS; i++) {
      const saved = localStorage.getItem(`medievalKingdomSlot_${i}`);
      if (saved) {
        try {
          const data = JSON.parse(saved);
          loadedSlots.push({
            index: i,
            exists: true,
            savedAt: data.savedAt,
            gold: data.playerResources?.gold || 0,
            totalBuildings: Object.values(data.buildingCounts || {}).reduce((a, b) => a + b, 0),
            techCount: data.researchedTechs?.length || 0,
            playtime: data.stats?.totalPlaytime || 0,
          });
        } catch (e) {
          console.warn(`[SaveManager] Slot ${i} corrupted:`, e.message);
          loadedSlots.push({ index: i, exists: false, corrupted: true });
        }
      } else {
        loadedSlots.push({ index: i, exists: false });
      }
    }
    setSlots(loadedSlots);
  };

  // Save to slot
  const saveToSlot = (slotIndex) => {
    const slot = slots.find(s => s.index === slotIndex);
    
    if (slot?.exists) {
      if (confirmAction?.type === 'overwrite' && confirmAction?.slotIndex === slotIndex) {
        // Confirmed - do the save
        const data = getSaveData();
        data.savedAt = Date.now();
        localStorage.setItem(`medievalKingdomSlot_${slotIndex}`, JSON.stringify(data));
        loadSlotMetadata();
        setConfirmAction(null);
        onNotification?.(`💾 Saved to Slot ${slotIndex + 1}`, 'success');
      } else {
        // Ask for confirmation
        setConfirmAction({ type: 'overwrite', slotIndex });
      }
    } else {
      // Empty slot - save directly
      const data = getSaveData();
      data.savedAt = Date.now();
      localStorage.setItem(`medievalKingdomSlot_${slotIndex}`, JSON.stringify(data));
      loadSlotMetadata();
      onNotification?.(`💾 Saved to Slot ${slotIndex + 1}`, 'success');
    }
  };

  // Load from slot
  const loadFromSlot = (slotIndex) => {
    const saved = localStorage.getItem(`medievalKingdomSlot_${slotIndex}`);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        loadSaveData(data);
        onNotification?.(`📂 Loaded Slot ${slotIndex + 1}`, 'success');
        setIsOpen(false);
      } catch (e) {
        onNotification?.('Failed to load save', 'error');
      }
    }
  };

  // Delete slot
  const deleteSlot = (slotIndex) => {
    if (confirmAction?.type === 'delete' && confirmAction?.slotIndex === slotIndex) {
      localStorage.removeItem(`medievalKingdomSlot_${slotIndex}`);
      loadSlotMetadata();
      setConfirmAction(null);
      onNotification?.(`🗑️ Deleted Slot ${slotIndex + 1}`, 'info');
    } else {
      setConfirmAction({ type: 'delete', slotIndex });
    }
  };

  // Export to file
  const exportToFile = () => {
    const data = getSaveData();
    data.savedAt = Date.now();
    data.exportedAt = Date.now();
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medieval-kingdom-save-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    onNotification?.('📥 Save exported to file', 'success');
  };

  // Import from file
  const importFromFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // File size limit (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setImportError('File too large (max 10MB)');
      onNotification?.('Save file too large', 'error');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);

        if (!data.playerResources || !data.buildingCounts) {
          throw new Error('Invalid save file format');
        }

        loadSaveData(data);
        onNotification?.('📤 Save imported successfully', 'success');
        setImportError(null);
        setIsOpen(false);
      } catch (err) {
        console.error('[SaveManager] Import failed:', err.message);
        setImportError('Invalid save file');
        onNotification?.('Failed to import save', 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Quick save
  const quickSave = () => {
    const data = getSaveData();
    data.savedAt = Date.now();
    localStorage.setItem('medievalKingdomSave', JSON.stringify(data));
    setLastAutoSave(Date.now());
    onNotification?.('💾 Quick saved', 'success');
  };

  // New game
  const handleNewGame = () => {
    if (confirmAction?.type === 'reset') {
      resetGame();
    } else {
      setConfirmAction({ type: 'reset' });
    }
  };

  // Cancel confirmation
  const cancelConfirm = () => {
    setConfirmAction(null);
  };

  // Format helpers
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatPlaytime = (ms) => {
    if (!ms) return '0m';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="save-manager" ref={menuRef}>
      <button 
        className="save-manager-btn"
        onClick={() => { setIsOpen(!isOpen); setConfirmAction(null); }}
        title="Save & Load"
      >
        💾
        {lastAutoSave && (
          <span className="save-indicator">•</span>
        )}
      </button>

      {isOpen && (
        <div className="save-menu">
          {/* Header */}
          <div className="save-menu-header">
            <span className="save-menu-title">💾 Save & Load</span>
            <span className="auto-save-status">{formatTimeAgo(lastAutoSave)}</span>
          </div>

          {/* Quick Actions */}
          <div className="save-actions-row">
            <button className="save-action quick" onClick={quickSave}>
              <span className="action-icon">⚡</span>
              <span>Quick Save</span>
            </button>
            <button className="save-action export" onClick={exportToFile}>
              <span className="action-icon">📥</span>
              <span>Export</span>
            </button>
            <button className="save-action import" onClick={() => fileInputRef.current?.click()}>
              <span className="action-icon">📤</span>
              <span>Import</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={importFromFile}
              style={{ display: 'none' }}
            />
          </div>

          {importError && <div className="save-error">{importError}</div>}

          {/* Divider */}
          <div className="save-divider">
            <span>Save Slots</span>
          </div>

          {/* Save Slots */}
          <div className="save-slots">
            {slots.map((slot) => {
              const isConfirmingDelete = confirmAction?.type === 'delete' && confirmAction?.slotIndex === slot.index;
              const isConfirmingOverwrite = confirmAction?.type === 'overwrite' && confirmAction?.slotIndex === slot.index;
              
              return (
                <div key={slot.index} className={`save-slot ${slot.exists ? 'has-save' : 'empty'}`}>
                  {/* Slot Header */}
                  <div className="slot-row">
                    <span className="slot-label">Slot {slot.index + 1}</span>
                    {slot.exists && (
                      <span className="slot-time">{formatTimeAgo(slot.savedAt)}</span>
                    )}
                  </div>
                  
                  {slot.exists ? (
                    <>
                      {/* Stats */}
                      <div className="slot-stats">
                        <span>🏰 {slot.totalBuildings}</span>
                        <span>🔬 {slot.techCount}</span>
                        <span>⏱️ {formatPlaytime(slot.playtime)}</span>
                      </div>
                      
                      {/* Actions or Confirmation */}
                      {isConfirmingDelete ? (
                        <div className="slot-confirm">
                          <span className="confirm-text">Delete this save?</span>
                          <button className="confirm-btn yes" onClick={() => deleteSlot(slot.index)}>Yes</button>
                          <button className="confirm-btn no" onClick={cancelConfirm}>No</button>
                        </div>
                      ) : isConfirmingOverwrite ? (
                        <div className="slot-confirm">
                          <span className="confirm-text">Overwrite?</span>
                          <button className="confirm-btn yes" onClick={() => saveToSlot(slot.index)}>Yes</button>
                          <button className="confirm-btn no" onClick={cancelConfirm}>No</button>
                        </div>
                      ) : (
                        <div className="slot-buttons">
                          <button className="slot-btn load" onClick={() => loadFromSlot(slot.index)}>Load</button>
                          <button className="slot-btn overwrite" onClick={() => saveToSlot(slot.index)}>Save</button>
                          <button className="slot-btn delete" onClick={() => deleteSlot(slot.index)}>🗑️</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <button className="slot-btn save-empty" onClick={() => saveToSlot(slot.index)}>
                      Save Here
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* New Game */}
          <div className="save-footer">
            {confirmAction?.type === 'reset' ? (
              <div className="reset-confirm">
                <span>Start new game?</span>
                <button className="confirm-btn yes" onClick={handleNewGame}>Yes</button>
                <button className="confirm-btn no" onClick={cancelConfirm}>No</button>
              </div>
            ) : (
              <button className="new-game-btn" onClick={handleNewGame}>
                🔄 New Game
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SaveManager;
